import React, { useEffect, useState } from 'react';
import { FileSpreadsheet, LayoutDashboard, GanttChart, Loader2 } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import {
  listImportsByTab,
  getFileKindForTab,
  getFileKindFromRow,
} from '../services/templatesImportsService';
import { templateImportFileName } from '../utils/templateImportFileName';

function formatCreatedAt(ts) {
  if (!ts) return '';
  try {
    const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString();
  } catch {
    return '';
  }
}

/**
 * Saves a remote URL as a file download instead of navigating / opening a new tab.
 * Uses a blob fetch when possible (works when the storage URL allows CORS GET).
 */
async function downloadRemoteFile(url, filename) {
  const safeName = filename?.trim() || 'download';
  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!res.ok) throw new Error(String(res.status));
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = safeName;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch {
    const a = document.createElement('a');
    a.href = url;
    a.download = safeName;
    a.rel = 'noopener noreferrer';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}

const PortalTechImportDownloads = ({ tabId }) => {
  const { darkMode } = useTheme();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const kind = getFileKindForTab(tabId);
  const kindLabel = kind === 'json' ? 'JSON' : 'CSV';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const list = await listImportsByTab(tabId);
        if (!cancelled) setRows(list);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setRows([]);
          setError(e.message || 'Could not load imports.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tabId]);

  if (loading) {
    return (
      <div
        className={`mt-6 flex items-center gap-2 rounded-lg border px-4 py-6 text-sm ${
          darkMode ? 'border-gray-700 bg-gray-800/60 text-gray-400' : 'border-gray-200 bg-white text-gray-500'
        }`}
      >
        <Loader2 className="h-5 w-5 animate-spin shrink-0" />
        Loading {kindLabel} imports…
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`mt-6 rounded-lg border px-4 py-3 text-sm ${
          darkMode ? 'border-red-900/60 bg-red-950/30 text-red-200' : 'border-red-200 bg-red-50 text-red-800'
        }`}
      >
        {error}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div
        className={`mt-6 rounded-lg border px-4 py-5 text-sm ${
          darkMode ? 'border-gray-700 bg-gray-800/40 text-gray-400' : 'border-gray-200 bg-gray-50 text-gray-600'
        }`}
      >
        No {kindLabel} imports yet. Uploads from CRM → Templates → Imports will appear here by type.
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-3">
      <h2
        className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}
      >
        {kindLabel} imports
      </h2>
      <ul className="space-y-3">
        {rows.map((row) => {
          const rowKind = getFileKindFromRow(row);
          const rowTab = row.tabId ?? row.importType;
          const Icon =
            rowKind === 'json'
              ? tabId === 'workflows' || rowTab === 'workflows'
                ? GanttChart
                : LayoutDashboard
              : FileSpreadsheet;
          return (
            <li
              key={row.id}
              className={`flex flex-col gap-2 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between ${
                darkMode ? 'border-gray-600 bg-gray-800/50' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex gap-3 min-w-0">
                <Icon
                  className={`h-10 w-10 shrink-0 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}
                />
                <div className="min-w-0">
                  <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {row.title || row.fileName}
                  </p>
                  {row.description ? (
                    <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {row.description}
                    </p>
                  ) : null}
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    {row.fileName}
                    {row.createdAt ? ` · ${formatCreatedAt(row.createdAt)}` : ''}
                  </p>
                </div>
              </div>
              {row.downloadURL && (
                <button
                  type="button"
                  onClick={() =>
                    downloadRemoteFile(row.downloadURL, templateImportFileName(row))
                  }
                  className="shrink-0 text-sm font-medium text-red-600 dark:text-red-400 hover:underline text-left bg-transparent border-0 cursor-pointer p-0"
                >
                  Download
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PortalTechImportDownloads;
